'use client';

/**
 * Tiny control to start/stop a host-side capture session
 * (`lib/workspaces/host-capture.ts`). `getDisplayMedia` requires a user
 * gesture, so the session can only be STARTED by a real click here; once
 * live, the `__capture` host builtin (widget-bridge) can grab any widget's
 * region any number of times with no further prompts.
 *
 * Gated behind `?capture=1` so it never shows for normal workspace users —
 * it's a capture/marketing tool, opt-in per visit.
 */

import { useEffect, useState } from 'react';
import {
  startCaptureSession,
  stopCaptureSession,
  hasCaptureSession,
  onCaptureSessionChange,
} from '@/lib/workspaces/host-capture';

export function HostCaptureControl() {
  const [enabled, setEnabled] = useState(false);
  const [on, setOn] = useState(false);

  useEffect(() => {
    try {
      setEnabled(new URLSearchParams(window.location.search).get('capture') === '1');
    } catch {
      /* ignore */
    }
    setOn(hasCaptureSession());
    return onCaptureSessionChange(() => setOn(hasCaptureSession()));
  }, []);

  if (!enabled) return null;

  return (
    <button
      type="button"
      data-host-capture-toggle
      onClick={async () => {
        if (hasCaptureSession()) {
          stopCaptureSession();
          return;
        }
        try {
          await startCaptureSession();
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('[host-capture] start failed:', err);
        }
      }}
      style={{
        position: 'fixed',
        bottom: 14,
        left: 14,
        zIndex: 99999,
        padding: '7px 12px',
        borderRadius: 9,
        background: on ? '#16a34a' : 'rgba(18,18,28,0.86)',
        color: '#fff',
        fontSize: 12,
        fontWeight: 600,
        border: '1px solid rgba(255,255,255,0.22)',
        cursor: 'pointer',
        boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
      }}
      title="Host-side widget capture (Region Capture). Click to grant a one-time screen share for this tab."
    >
      {on ? '● capture session live' : '○ start capture session'}
    </button>
  );
}
